import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme,
} from "@mui/material";
import moment from "moment";
import { toast } from "react-toastify";
import { GET, POST } from "../../api/ApiService";
import ButtonCommon from "../../shared/ButtonCommon";
import MainHeader from "../../shared/MainHeader";
import SearchBar from "../../shared/SearchBar";

const RESEND_COOLDOWN_MS = 5 * 60 * 1000;

const tableHeaders = [
  { id: "recipient", label: "Recipient", sortable: true, minWidth: 220 },
  { id: "subject", label: "Subject", sortable: true, minWidth: 220 },
  { id: "body", label: "Email Body", sortable: false, minWidth: 320 },
  {
    id: "errorMessage",
    label: "Failure Reason",
    sortable: true,
    minWidth: 280,
  },
  { id: "timestamp", label: "Failed At", sortable: true, minWidth: 180 },
  { id: "action", label: "Action", sortable: false, minWidth: 150 },
];

const getRecipient = (row) => {
  return Array.isArray(row.recipients) ? row.recipients.join(", ") : "";
};

const getComparableValue = (row, field) => {
  switch (field) {
    case "recipient":
      return getRecipient(row).toLowerCase();
    case "subject":
      return (row.subject || "").toLowerCase();
    case "body":
      return (row.body || "").toLowerCase();
    case "errorMessage":
      return (row.errorMessage || "").toLowerCase();
    case "timestamp":
      return row.timestamp ? new Date(row.timestamp).getTime() : 0;
    default:
      return "";
  }
};

const formatCooldown = (remainingMilliseconds) => {
  const totalSeconds = Math.ceil(remainingMilliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0"
  )}`;
};

const EmailStatus = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resendingId, setResendingId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterField, setFilterField] = useState("all");
  const [sortBy, setSortBy] = useState("timestamp");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentTime, setCurrentTime] = useState(Date.now());

  const fetchEmailStatuses = useCallback(async () => {
    setLoading(true);

    try {
      const response = await GET("email-status");
      const fetchedAt = Date.now();
      setRows(
        (response?.data || []).map((row) => ({
          ...row,
          cooldownUntil:
            fetchedAt + Math.max(0, row.retryAfterSeconds || 0) * 1000,
        }))
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to load failed email sends."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmailStatuses();
  }, [fetchEmailStatuses]);

  useEffect(() => {
    const intervalId = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const handleResend = async (row) => {
    const recipient = getRecipient(row);
    const shouldResend = window.confirm(
      `Resend "${row.subject}" to ${recipient}?`
    );

    if (!shouldResend) {
      return;
    }

    setResendingId(row.id);

    try {
      const response = await POST(`email-status/resend/${row.id}`, {});
      toast.success(response?.message || "Email resent successfully.");
      await fetchEmailStatuses();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to resend the email."
      );
    } finally {
      setResendingId("");
    }
  };

  const filteredAndSortedRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredRows = rows.filter((row) => {
      if (!normalizedSearch) {
        return true;
      }

      const searchableValues =
        filterField === "all"
          ? [
              getRecipient(row),
              row.subject || "",
              row.body || "",
              row.errorMessage || "",
            ]
          : [getComparableValue(row, filterField)];

      return searchableValues.some((value) =>
        String(value).toLowerCase().includes(normalizedSearch)
      );
    });

    return [...filteredRows].sort((firstRow, secondRow) => {
      const firstValue = getComparableValue(firstRow, sortBy);
      const secondValue = getComparableValue(secondRow, sortBy);

      if (firstValue === secondValue) {
        return 0;
      }

      const comparison = firstValue < secondValue ? -1 : 1;
      return sortOrder === "asc" ? comparison : comparison * -1;
    });
  }, [filterField, rows, searchTerm, sortBy, sortOrder]);

  const paginatedRows = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedRows.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedRows, page, rowsPerPage]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((currentOrder) =>
        currentOrder === "asc" ? "desc" : "asc"
      );
    } else {
      setSortBy(field);
      setSortOrder(field === "timestamp" ? "desc" : "asc");
    }

    setPage(0);
  };

  const getCooldownRemaining = (row) => {
    if (Number.isFinite(row.cooldownUntil)) {
      return Math.max(0, row.cooldownUntil - currentTime);
    }

    const failedAt = row.timestamp
      ? new Date(row.timestamp).getTime()
      : Number.NaN;

    return Number.isNaN(failedAt)
      ? 0
      : Math.max(0, failedAt + RESEND_COOLDOWN_MS - currentTime);
  };

  const isDarkMode = theme.palette.mode === "dark";
  const tableBorderColor = isDarkMode ? theme.palette.grey[800] : "#E6E6E6";
  const tableHeaderBackground = isDarkMode
    ? theme.palette.grey[900]
    : "#F8F9FB";
  const tableRowHoverBackground = isDarkMode
    ? "rgba(61, 203, 177, 0.08)"
    : "#FAFCFF";
  const errorTextColor = isDarkMode ? "#FCA5A5" : "#B42318";

  return (
    <>
      <MainHeader headingText="Failed Email Sends" />
      <Stack gap={3} mt={3}>
        <Alert severity="info">
          This page shows messages the configured email provider rejected. An
          accepted message may still be delayed, bounced, or blocked after
          submission.
        </Alert>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          gap={2}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            gap={2}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <SearchBar
              searchHeight="40px"
              placeholder="Search failed email sends"
              fullWidth={false}
              searchValue={(value) => {
                setSearchTerm(value);
                setPage(0);
              }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="email-status-filter-label">Filter By</InputLabel>
              <Select
                labelId="email-status-filter-label"
                value={filterField}
                label="Filter By"
                onChange={(event) => {
                  setFilterField(event.target.value);
                  setPage(0);
                }}
                sx={{ backgroundColor: theme.palette.background.paper }}
              >
                <MenuItem value="all">All Fields</MenuItem>
                <MenuItem value="recipient">Recipient</MenuItem>
                <MenuItem value="subject">Subject</MenuItem>
                <MenuItem value="body">Email Body</MenuItem>
                <MenuItem value="errorMessage">Failure Reason</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <ButtonCommon
            btnClicked={fetchEmailStatuses}
            disabled={loading}
            startIcon={false}
          >
            Refresh
          </ButtonCommon>
        </Stack>

        {loading ? (
          <Stack justifyContent="center" alignItems="center" minHeight={240}>
            <CircularProgress />
          </Stack>
        ) : filteredAndSortedRows.length ? (
          <>
            <TableContainer
              sx={{
                border: `1px solid ${tableBorderColor}`,
                borderRadius: 1,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableCell
                        key={header.id}
                        align={header.id === "action" ? "center" : "left"}
                        sx={{
                          minWidth: header.minWidth,
                          backgroundColor: tableHeaderBackground,
                          fontWeight: 700,
                          borderBottom: `1px solid ${tableBorderColor}`,
                        }}
                      >
                        {header.sortable ? (
                          <TableSortLabel
                            active={sortBy === header.id}
                            direction={sortBy === header.id ? sortOrder : "asc"}
                            onClick={() => handleSort(header.id)}
                          >
                            {header.label}
                          </TableSortLabel>
                        ) : (
                          header.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.map((row) => {
                    const cooldownRemaining = getCooldownRemaining(row);
                    const cooldownActive = cooldownRemaining > 0;
                    const hasContent =
                      (row.body && row.body.length >= 5) ||
                      (row.htmlBody && row.htmlBody.length >= 5);

                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          "&:last-child td": { borderBottom: 0 },
                          "&:hover": {
                            backgroundColor: tableRowHoverBackground,
                          },
                        }}
                      >
                        <TableCell sx={{ verticalAlign: "top" }}>
                          {getRecipient(row) || "-"}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          {row.subject || "-"}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 380,
                            verticalAlign: "top",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              maxHeight: 120,
                              overflowY: "auto",
                            }}
                          >
                            {row.body || "HTML content only"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 280,
                            verticalAlign: "top",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              color: errorTextColor,
                              fontWeight: 500,
                            }}
                          >
                            {row.errorMessage || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          {row.timestamp
                            ? moment.utc(row.timestamp).local().format("LLL")
                            : "-"}
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: "top" }}>
                          <ButtonCommon
                            btnClicked={() => handleResend(row)}
                            disabled={
                              resendingId === row.id ||
                              cooldownActive ||
                              !row.subject ||
                              !hasContent
                            }
                            startIcon={false}
                          >
                            {resendingId === row.id
                              ? "Resending..."
                              : cooldownActive
                                ? `Try after ${formatCooldown(
                                    cooldownRemaining
                                  )}`
                                : "Resend"}
                          </ButtonCommon>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredAndSortedRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number.parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </>
        ) : (
          <Stack justifyContent="center" alignItems="center" minHeight={180}>
            <Typography>
              {rows.length
                ? "No matching failed email sends found."
                : "No failed email sends found."}
            </Typography>
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default EmailStatus;
